import { z } from "zod";
import { FIELD_TYPES } from "../constants.ts";
import {
  DbBool,
  EntityTypeSchema,
  HexColorSchema,
  IsoDateTime,
  LocalIdSchema,
  PositionSchema,
  jsonColumn,
} from "./common.ts";

export const FieldTypeSchema = z.enum(FIELD_TYPES);

export const SelectOptionSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(120),
  color: HexColorSchema.optional(),
});

export type SelectOption = z.infer<typeof SelectOptionSchema>;

/** Per-type extras. All optional; only the keys a given type reads are used. */
export const FieldConfigSchema = z.object({
  options: z.array(SelectOptionSchema).max(200).optional(),
  /** Textarea height for `longtext`. */
  rows: z.number().int().min(1).max(40).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  /** Number of decimal places to render for `number`. */
  precision: z.number().int().min(0).max(6).optional(),
  placeholder: z.string().max(200).optional(),
});

export type FieldConfig = z.infer<typeof FieldConfigSchema>;

export const FieldDefSchema = z.object({
  id: LocalIdSchema,
  /** Stable slug. Saved views reference this, so renaming the label is free. */
  key: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, "Key must be lowercase alphanumeric with - or _"),
  label: z.string().min(1).max(120),
  type: FieldTypeSchema,
  config: jsonColumn(FieldConfigSchema).nullable().default(null),
  group_name: z.string().max(120).nullable(),
  position: PositionSchema,
  entity_types: jsonColumn(z.array(EntityTypeSchema).min(1)),
  /** Hides the field without destroying its values. */
  archived: DbBool,
  created_at: IsoDateTime,
});

export type FieldDef = z.infer<typeof FieldDefSchema>;

export const FieldDefCreateSchema = z.object({
  label: z.string().trim().min(1).max(120),
  type: FieldTypeSchema,
  /** Derived from the label when omitted. */
  key: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, "Key must be lowercase alphanumeric with - or _")
    .optional(),
  config: FieldConfigSchema.optional(),
  group_name: z.string().trim().max(120).nullable().optional(),
  entity_types: z.array(EntityTypeSchema).min(1).default(["track"]),
  after_id: LocalIdSchema.nullable().optional(),
  before_id: LocalIdSchema.nullable().optional(),
});

export type FieldDefCreate = z.infer<typeof FieldDefCreateSchema>;

/**
 * `type` is deliberately absent — changing a field's type would silently
 * invalidate every stored value. Delete and recreate instead.
 */
export const FieldDefUpdateSchema = z
  .object({
    label: z.string().trim().min(1).max(120),
    config: FieldConfigSchema.nullable(),
    group_name: z.string().trim().max(120).nullable(),
    entity_types: z.array(EntityTypeSchema).min(1),
    archived: z.boolean(),
    after_id: LocalIdSchema.nullable(),
    before_id: LocalIdSchema.nullable(),
  })
  .partial();

export type FieldDefUpdate = z.infer<typeof FieldDefUpdateSchema>;
